import type { CylinderSurface } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { cylinderRectOnPhoto, type PhotoDimensions } from '@/entities/edit-area';
import { closedPathOf } from '../model/svg-path';

interface CylinderSurfaceOverlayProps {
  readonly surface: CylinderSurface;
  readonly photoSize: PhotoDimensions;
}

/**
 * 원통 표면의 실루엣 — 프레임 [0, W]×[0, H] 전체를 투영한 닫힌 곡선(cylinderRectOnPhoto)과 저장된 보이는 사각형(점선 안내).
 * 포인터를 받지 않는다: 표면은 사진 위에서 고르거나 고치지 않고 '다시 추출하기'·'표면 삭제'·고급 수치 칸으로만 바꾼다.
 */
export const CylinderSurfaceOverlay = ({
  surface,
  photoSize,
}: CylinderSurfaceOverlayProps): ReactElement | null => {
  const silhouette = cylinderRectOnPhoto(surface, photoSize, surface.realSize, {
    xMm: 0,
    yMm: 0,
  })[0];
  if (silhouette === undefined) return null;
  const [topLeft, , bottomRight] = surface.points;
  return (
    <g pointerEvents="none">
      <path
        d={closedPathOf(silhouette)}
        fill="#64748b10"
        stroke="#64748b"
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
        data-testid={`cylinder-surface-${surface.id}`}
      />
      <rect
        x={(topLeft.x * photoSize.widthPx) / 100}
        y={(topLeft.y * photoSize.heightPx) / 100}
        width={((bottomRight.x - topLeft.x) * photoSize.widthPx) / 100}
        height={((bottomRight.y - topLeft.y) * photoSize.heightPx) / 100}
        fill="none"
        stroke="#64748b"
        strokeDasharray="4 4"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
        data-testid={`cylinder-visible-rect-${surface.id}`}
      />
    </g>
  );
};
